import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DevicesIcon from '@mui/icons-material/Devices';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import BugReportIcon from '@mui/icons-material/BugReport';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const DigitalServices: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const digitalServices = [
    {
      title: "Digital Transformation",
      description: "Our digital transformation services help organizations leverage technology to evolve their business models, processes, and customer experiences.",
      icon: <TrackChangesIcon fontSize="large" />,
      benefits: [
        "Streamlined business processes",
        "Enhanced customer experiences",
        "Data-driven decision making",
        "Improved operational efficiency"
      ],
      path: "/services/digital/transformation"
    },
    {
      title: "Desktop Development",
      description: "We provide efficient, customized and user-friendly desktop applications that can run both offline and independent of web browsers.",
      icon: <ComputerIcon fontSize="large" />,
      benefits: [
        "Custom solutions tailored to your needs",
        "Robust offline functionality",
        "Seamless integration with existing systems",
        "Enhanced security and performance"
      ],
      path: "/services/digital/desktop"
    },
    {
      title: "Mobile App Development",
      description: "Our mobile app development services create engaging, user-friendly applications for iOS and Android platforms that enhance your business reach.",
      icon: <PhoneIphoneIcon fontSize="large" />,
      benefits: [
        "Cross-platform compatibility",
        "Intuitive user interfaces",
        "Scalable architecture",
        "Continuous support and updates"
      ],
      path: "/services/digital/mobile"
    },
    {
      title: "Quality Assurance Services",
      description: "Our Software Quality Assurance (SQA) team ensures your products meet the highest quality standards through comprehensive testing and validation.",
      icon: <BugReportIcon fontSize="large" />,
      benefits: [
        "Reduced software defects",
        "Improved user satisfaction",
        "Streamlined development process",
        "Enhanced product reliability"
      ],
      path: "/services/digital/qa"
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Digital Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive digital solutions to transform your business and enhance your competitive edge
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Our Digital Services
              </Typography>
              <Typography paragraph>
                In today's rapidly evolving digital landscape, businesses need innovative technology 
                solutions to stay competitive and meet changing customer expectations. Right Track 
                Technologies offers comprehensive digital services designed to transform your business, 
                enhance efficiency, and drive growth.
              </Typography>
              <Typography paragraph>
                Our team of skilled professionals combines technical expertise with industry knowledge 
                to deliver solutions that address your specific business challenges. Whether you need 
                desktop applications, mobile solutions, quality assurance, or a complete digital 
                transformation strategy, we have the capabilities to turn your vision into reality.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Digital Services"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Services Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Our Digital Solutions
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Explore our range of digital services designed to help your organization innovate,
              optimize operations, and deliver exceptional customer experiences.
            </Typography>
          </motion.div>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
            {digitalServices.map((service, index) => (
              <Box key={index} sx={{ width: { xs: '100%', md: '50%' }, px: 2, mb: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 5,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#1976d215',
                      color: '#1976d2',
                      mb: 3,
                    }}>
                      {service.icon}
                    </Box>
                    
                    <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom color="#0c2340">
                      {service.title}
                    </Typography>
                    
                    <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                      {service.description}
                    </Typography>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h6" gutterBottom fontWeight="medium">
                      Key Benefits:
                    </Typography>
                    
                    <List sx={{ mb: 3 }}>
                      {service.benefits.map((benefit, idx) => (
                        <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={benefit} />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        component={Link}
                        to={service.path}
                        sx={{
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: '#1976d215',
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Digital Transformation Approach */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Our Digital Transformation Approach
              </Typography>
              <Typography paragraph>
                At Right Track Technologies Limited, we follow a structured approach to digital transformation 
                that ensures successful outcomes and minimizes risks. Our comprehensive approach addresses 
                both technological implementation and the essential people and process aspects of 
                transformation.
              </Typography>
              
              <List>
                {[
                  "Discovery: We analyze your current state, identify challenges, and define objectives.",
                  "Strategy: We develop a customized roadmap aligned with your business goals.",
                  "Design: We create user-centric solutions that address your specific needs.",
                  "Development: We build robust, scalable, and secure digital solutions.",
                  "Testing: We ensure quality and reliability through rigorous testing.",
                  "Deployment: We implement solutions with minimal disruption to your operations.",
                  "Support: We provide ongoing maintenance and enhancements to ensure long-term success."
                ].map((step, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={step} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Digital Transformation Approach"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        backgroundColor: '#f5f7fa', 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Transform Your Business?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our digital experts are ready to help you implement innovative solutions that drive 
                business growth and competitive advantage. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=digital"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  Explore Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitalServices; 